import type { ComponentType } from 'react';
import { useTabListKeys } from '@cwos/ui-admin';
import { useQuery } from '@tanstack/react-query';
import { dashboard, schedule } from '@cwos/api-client';
import { useAnalyticsControls } from './AnalyticsContext.js';
import { listPhase } from '../../listState.js';
import { usePermissions } from '../../permissions.js';
import { pickerState } from '../../pickerState.js';
import {
  ANALYTICS_PRESETS,
  ANALYTICS_LENSES,
  ANALYTICS_VIEWS,
  isCustomPreset,
  type AnalyticsPreset,
  type AnalyticsView,
} from './analyticsRange.js';
import { OverviewView } from './views/OverviewView.js';
import { StaffView } from './views/StaffView.js';
import { ScheduleView } from './views/ScheduleView.js';
import { ClientsView } from './views/ClientsView.js';
import { CareGapsView } from './views/CareGapsView.js';
import { DocumentationView } from './views/DocumentationView.js';
import { ImpactView } from './views/ImpactView.js';
import { MonitorView } from './views/MonitorView.js';

/**
 * WHICH TABS THE SERVER WILL ACTUALLY SERVE THIS READER.
 *
 * `ANALYTICS_SURFACE_READ` (features/permissions.ts) records the fact this table
 * is the other half of: the API's `AGGREGATE_READ` is
 * `['dashboard:read', 'analytics:read']`, so an `analytics:read`-only reader —
 * the shipped `analyst` role — opens this workspace and every aggregate route
 * behind it. `ROW_LEVEL_ROUTES` in the same API file is `dashboard:read` ONLY,
 * and it is not widened by `EQUIVALENT_READ_KEYS`, deliberately: the equivalence
 * exists because a route family whose guard is a LIST is not honestly described
 * by one member of it, and the row-level family's guard is not a list.
 *
 * ONE VIEW IN THIS WORKSPACE IS BEHIND THAT FAMILY AT ITS FRONT DOOR.
 * `DocumentationView`'s FIRST read is `GET /api/dashboard/documentation`, which
 * is row-level ("summary, but embeds byWorker[]" — accessPolicy.ts) and refuses
 * an analyst. There is nothing left of the tab once it is refused: the two stat
 * rows, the drilldown and the trend all hang off that summary. So the analyst
 * met one dead tab in a strip of eight otherwise-readable ones, and a dead tab
 * is worse than an absent one — it reads as "you were supposed to get this".
 *
 * WHY THIS IS A LIST OF ONE AND NOT A `permission` FIELD ON `ANALYTICS_VIEWS`.
 * The other seven views are readable with either key, so a field would be
 * `undefined` seven times out of eight and would invite the eighth to be filled
 * in by guesswork. Naming the exception, with the route and the API file that
 * classifies it, is the same shape `EQUIVALENT_READ_KEYS` uses for the same
 * reason — the difference between what a screen names and what the server takes
 * is recorded once, next to the server file that decides it.
 *
 * A ROW-LEVEL READER STILL GETS THE TAB. This hides nothing from anybody the
 * API would serve; it is default-deny only against a key the route rejects.
 */
const ROW_LEVEL_VIEWS: Partial<Record<AnalyticsView, string>> = {
  // GET /api/dashboard/documentation + /documentation/open — ROW_LEVEL_ROUTES.
  documentation: 'dashboard:read',
};

const VIEWS: Record<AnalyticsView, ComponentType> = {
  pulse: OverviewView,
  staff: StaffView,
  schedule: ScheduleView,
  clients: ClientsView,
  careGaps: CareGapsView,
  documentation: DocumentationView,
  impact: ImpactView,
  monitor: MonitorView,
};

/**
 * AnalyticsShell — the multi-view analytics workspace. The date controls and
 * active view live in URL search params (via AnalyticsProvider), so the selected
 * period persists across view switches and deep links. The weekly charts
 * follow the same period, starting where the organization's records begin, with
 * the week in progress drawn as "week to date".
 *
 * Organizational filters (programs+sites follow-up): a program filter applies
 * to every view; the site filter is offered only on the Schedule view, because
 * shifts are the only analytics data that carries a site. Either is offered only
 * when the organization's records carry that dimension at all. The dropdown options
 * come from the server and are already narrowed by the caller's grants — a
 * program-scoped principal only ever sees their own programs/sites listed.
 */
export function AnalyticsShell() {
  const { view, preset, from, to, setView, setPreset, setCustomRange } = useAnalyticsControls();
  const { has } = usePermissions();

  /**
   * `has`, NOT `mayOpenScreen`. `mayOpenScreen` asks the widened question — the
   * one that answers `true` for an `analytics:read` holder asked about
   * `dashboard:read` — and it is the RIGHT question for the rail entry that
   * opens this workspace. It is the wrong one here: the widening is declared for
   * aggregate routes and this tab's route is not one. Reaching for it because
   * every other gate in this app uses it is exactly how the analyst gets their
   * 403 back.
   *
   * `has` is also `false` while `/auth/me` is in flight and `false` when it
   * failed, so a cold load hides the tab for a moment rather than offering a
   * door that may not open. Default-deny on a tab is the correct direction; the
   * tab reappears when the set arrives.
   */
  const tabs = ANALYTICS_VIEWS.filter((v) => has(ROW_LEVEL_VIEWS[v.value]));
  /**
   * THE FIVE LENSES over the same permitted views (owner Phase G ruling). A
   * lens is offered when ANY of its member views is — the same
   * never-offer-a-403 rule the row-level filter above enforces per view — and
   * clicking it lands on its first permitted member. The current lens is
   * DERIVED from the current view, so a legacy `?view=` deep link lights the
   * right lens with nothing stored twice.
   */
  const lenses = ANALYTICS_LENSES.map((lens) => ({
    ...lens,
    views: lens.views.filter((v) => tabs.some((t) => t.value === v)),
  })).filter((lens) => lens.views.length > 0);
  const activeLens = lenses.find((lens) => lens.views.includes(view)) ?? null;
  const subViews = activeLens && activeLens.views.length > 1 ? activeLens.views : [];
  const labelOf = (v: AnalyticsView) => ANALYTICS_VIEWS.find((t) => t.value === v)?.label ?? v;

  const ActiveView = VIEWS[view];
  const viewTabs = useTabListKeys<HTMLElement>();

  return (
    <div>
      <div className="an-head">
        <h1 className="rn-card-title">Analytics</h1>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          A read-only view of how the work is actually delivered — every number opens to the rows
          behind it.
        </p>
      </div>

      <DateControls
        preset={preset}
        from={from}
        to={to}
        onPreset={setPreset}
        onCustom={setCustomRange}
      />

      <FilterControls />

      {/* `aria-pressed` is dropped: it is not valid on `role="tab"`, and a tab
          that reports both a pressed and a selected state gives AT two answers
          to one question. `aria-controls`/`role="tabpanel"` now connect the
          strip to the view it actually swaps. */}
      <nav
        className="rn-tabs"
        role="tablist"
        aria-label="Analytics lenses"
        style={{ marginTop: 'var(--space-12)' }}
        {...viewTabs}
      >
        {lenses.map((lens) => (
          <button
            key={lens.key}
            type="button"
            role="tab"
            id={`analytics-tab-${lens.views[0]!}`}
            aria-controls="analytics-panel"
            className="rn-tab"
            aria-selected={activeLens?.key === lens.key}
            onClick={() => setView(lens.views[0]!)}
          >
            {lens.label}
          </button>
        ))}
      </nav>
      {subViews.length > 0 ? (
        <div
          className="rn-tabs an-subviews"
          role="group"
          aria-label={`${activeLens!.label} views`}
          style={{ marginTop: 'var(--space-8)' }}
        >
          {subViews.map((v) => (
            <button
              key={v}
              type="button"
              className="rn-tab"
              aria-pressed={view === v}
              onClick={() => setView(v)}
            >
              {labelOf(v)}
            </button>
          ))}
        </div>
      ) : null}

      {/* A deep link can still name a hidden view, and the panel is then labelled
          by a tab that is not in the document. The SERVER stays the authority on
          whether that view answers — `DocumentationView` renders the refusal it
          is given, which is a better sentence than one this shell invents — so
          the view is still mounted; what is dropped is the dangling reference. */}
      <div
        id="analytics-panel"
        role="tabpanel"
        aria-labelledby={activeLens ? `analytics-tab-${activeLens.views[0]!}` : undefined}
        style={{ marginTop: 'var(--space-20)' }}
      >
        <ActiveView />
      </div>
    </div>
  );
}

function DateControls({
  preset,
  from,
  to,
  onPreset,
  onCustom,
}: {
  preset: AnalyticsPreset;
  from: string;
  to: string;
  onPreset: (preset: AnalyticsPreset) => void;
  onCustom: (from: string, to: string) => void;
}) {
  return (
    <div className="an-dates">
      {/* A filter, not a tab strip: nothing here swaps a panel. `role="tablist"`
          with `aria-pressed` children was invalid twice over — `aria-pressed` is
          not allowed on `role="tab"`, and a tablist whose children are not tabs
          owns nothing. A labelled group of toggle buttons is what this is. */}
      <div
        className="rn-tabs"
        role="group"
        aria-label="Reporting period"
        style={{ marginBottom: 0 }}
      >
        {ANALYTICS_PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            className="rn-tab"
            aria-pressed={preset === p.value}
            onClick={() => onPreset(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>
      {isCustomPreset(preset) ? (
        <div
          style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <input
            type="date"
            value={from}
            onChange={(e) => onCustom(e.target.value, to)}
            aria-label="From date"
            className="an-date-input"
          />
          <span className="rn-muted">to</span>
          <input
            type="date"
            value={to}
            onChange={(e) => onCustom(from, e.target.value)}
            aria-label="To date"
            className="an-date-input"
          />
        </div>
      ) : null}
    </div>
  );
}

/** Program (all views) + site (schedule only) filters with a scope honesty note. */
function FilterControls() {
  const { view, programId, siteId, setProgram, setSite } = useAnalyticsControls();
  const { has } = usePermissions();

  const options = useQuery({
    queryKey: ['analytics', 'filter-options'],
    queryFn: () => dashboard.analyticsFilterOptions(),
    staleTime: 60_000,
  });

  /**
   * OFFER A FILTER ONLY WHERE THE RECORDS CARRY IT (owner ruling). A program
   * filter over visits that carry no program — every Ritten visit at
   * Generation Hope — narrows every figure to zero; that is a fabricated
   * answer, not a filter. Read off the all-time impact breakdown: a program
   * filter when some program served a client, a site filter when some
   * delivered service recorded a site or any shift exists (shifts carry their
   * own site, which impact cannot see). Unknown (loading, failed, hidden, or no
   * org-wide shift read) keeps the filter, and an applied filter always stays
   * so it can be cleared.
   * ponytail: derived from impact rows and a one-shift probe; move to a
   * has-data flag on filter-options when the dashboard API carries one.
   */
  const usage = useQuery({
    queryKey: ['analytics', 'impact', { preset: 'all' }],
    queryFn: () => dashboard.analyticsImpact({ preset: 'all' }),
    staleTime: 5 * 60_000,
  });
  const shiftProbe = useQuery({
    queryKey: ['analytics', 'shift-probe'],
    queryFn: () => schedule.shifts({ limit: 1 }),
    enabled: view === 'schedule' && has('schedule:manage'),
    staleTime: 5 * 60_000,
  });
  const used = usage.data;
  const offerProgram =
    programId !== '' ||
    used === undefined ||
    used.byProgram.some((p) => p.programId !== null && p.clientsServed > 0);
  const offerSite =
    siteId !== '' ||
    shiftProbe.data === undefined ||
    shiftProbe.data.length > 0 ||
    used === undefined ||
    used.appointments == null ||
    used.appointmentsWithoutSite == null ||
    used.appointments > used.appointmentsWithoutSite;

  const data = options.data;
  // The two dropdowns were `(data?.x ?? []).map(...)`: a refused, failed or
  // in-flight options read rendered an organization with programs as one
  // without.
  const programPicker = pickerState(
    listPhase(options, data === undefined ? undefined : data.programs.length),
    'programs',
    'All programs',
  );
  const sitePicker = pickerState(
    listPhase(options, data === undefined ? undefined : data.sites.length),
    'sites',
    'All sites',
  );

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-10)',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginTop: 'var(--space-12)',
      }}
    >
      {offerProgram ? (
        <label style={{ display: 'inline-flex', gap: 'var(--space-6)', alignItems: 'center' }}>
          <span className="rn-muted">Program</span>
          <select
            aria-label="Program filter"
            value={programId}
            disabled={programPicker.disabled}
            onChange={(e) => setProgram(e.target.value)}
          >
            <option value="">
              {programPicker.disabled ? programPicker.placeholder : 'All programs'}
            </option>
            {(data?.programs ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.code ? ` (${p.code})` : ''}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {view === 'schedule' && offerSite ? (
        <label style={{ display: 'inline-flex', gap: 'var(--space-6)', alignItems: 'center' }}>
          <span className="rn-muted">Site</span>
          <select
            aria-label="Site filter"
            value={siteId}
            disabled={sitePicker.disabled}
            onChange={(e) => setSite(e.target.value)}
          >
            <option value="">{sitePicker.disabled ? sitePicker.placeholder : 'All sites'}</option>
            {(data?.sites ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {/* This options query had no loading and no error branch. On failure the
          two dropdowns collapsed to "All programs" / "All sites" — an
          organisation with programs looked like one without — and, worse, the
          scope note below vanished with them, which is exactly the note a
          scoped reader needs in order to interpret the numbers on the screen.
          Failing means we do not know whether the reader is scoped, and that is
          what it says. */}
      {options.isError && data === undefined ? (
        <span className="rn-muted" role="status">
          Filters couldn’t be loaded, so this view is unfiltered and we cannot say whether your
          access narrows it.
        </span>
      ) : data === undefined ? (
        // The missing fourth state. While the options read is in flight,
        // `isError` is false and `data` is undefined, so the note rendered as
        // `null` — and for that window a SCOPED reader was shown an
        // unqualified, organization-shaped screen. Silence read as "nothing
        // narrows this".
        <span className="rn-muted" role="status">
          Checking whether your access narrows this view…
        </span>
      ) : data.scoped.programs || data.scoped.sites ? (
        <span className="rn-muted" role="note">
          {/* `scoped.sites` was never read anywhere in the app, and it is the
              half that matters most: for a site-scoped caller every view except
              Schedule calls `filterScope(principal, false)`, which returns no
              accessible programs and matches nothing in SQL — so those views
              fail closed as a complete-looking page of zeros. A reader has to be
              told that before they read the numbers, not after. */}
          {data.scoped.sites && !data.scoped.programs
            ? 'Your access is limited to particular sites. Only the Schedule view can honour that, so figures on the other views may count nothing at all rather than the organization.'
            : data.scoped.sites
              ? 'Restricted to the programs and sites you can access, so these are not organization-wide figures.'
              : 'Restricted to the programs you can access, so these are not organization-wide figures.'}
        </span>
      ) : null}
    </div>
  );
}
